import { memo, useCallback, useEffect, useState } from 'react';

import { Input } from '@/components/ui/input';
import i18n from '@/core/i18n';
import { cn } from '@/lib/utils';

import { validateApprovedInput } from '../../../helpers/approved-hours-validation.helper';
import { formatTimeEntryDuration } from '../../../helpers/format-time-track.helper';
import { getInvalidApprovedInputValue } from '../../../helpers/invalid-approved-inputs.helper';
import { useApprovalsStore } from '../../../store/approvals.store';
import { ApprovalsTableMeta, ApprovedHoursField } from '../../../types/approvals-table.types';
import { ApprovalTimeEntry, TimeEntryStatus } from '../../../types/time-tracking-approvals';
import { ApprovedValueLabel } from './approved-value-label';

interface ApprovedDurationInputProps {
  label: string;
  labelTooltip: string;
  ariaLabel: string;
  /** The value currently in effect — the pending edit if there is one, else the saved value. */
  hours: number;
  /**
   * Unparseable text this input held when it was last on screen, from the store. Restored on
   * mount so collapsing a row (or scrolling it out of a virtualized list) never discards the
   * correction the user was in the middle of making.
   */
  storedInvalidValue: string | undefined;
  isReadOnly: boolean;
  onCommit: (hours: number) => void;
  onValidityChange: (rawValue: string | null) => void;
}

/**
 * One of the two approved-duration inputs.
 *
 * Displays the value in the same `2h 30m` format used across the table, but on focus accepts the
 * free-form duration grammar from quick-add (`3h 30m`, `600m`, `1.5`, `2:30`, etc.). Parsed
 * minutes are converted back to decimal hours to keep the commit contract unchanged.
 *
 * Deliberately `h-6` (not the `h-7` of a standalone field): two of these are stacked inside the
 * existing Approved column, and keeping the table compact is a hard requirement.
 */
function ApprovedDurationInput({
  label,
  labelTooltip,
  ariaLabel,
  hours,
  storedInvalidValue,
  isReadOnly,
  onCommit,
  onValidityChange,
}: ApprovedDurationInputProps) {
  const formatted = formatTimeEntryDuration(hours);
  // While idle the field shows the formatted duration; on focus it switches to the raw text
  // being typed. It starts from the stored unparseable text when there is one, so a remount
  // resumes exactly where the user left off instead of snapping back to the saved value.
  const [value, setValue] = useState(storedInvalidValue ?? formatted);
  const [isFocused, setIsFocused] = useState(false);
  // i18n key of the validation message, or null when the input is valid.
  const [errorKey, setErrorKey] = useState<string | null>(
    storedInvalidValue === undefined ? null : validateApprovedInput(storedInvalidValue).errorKey
  );

  // Re-sync from the saved/edited value only while the field is idle AND valid. Without the
  // error guard, blurring an unparseable entry would overwrite the user's text with the last
  // good value while the error message stayed on screen — an "Invalid format" complaint
  // pointing at a perfectly valid-looking duration.
  useEffect(() => {
    if (!isFocused && errorKey === null) setValue(formatted);
  }, [formatted, isFocused, errorKey]);

  // Publish validity so the footer, the row's approve button and the ancestor warning icons
  // can all react. Deliberately NOT cleared on unmount: a collapsed row still has a broken
  // value, and forgetting it here is exactly how the correction used to get lost.
  useEffect(() => {
    onValidityChange(errorKey === null ? null : value);
  }, [errorKey, value, onValidityChange]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  // Validate as the user types so the error (and accepted formats) show live. An empty field is
  // "nothing entered yet", not a mistake: flagging it would light up the row's error, the blocked
  // approve buttons and the ancestor warnings the instant someone selects-all to retype a value.
  const handleChange = useCallback((next: string) => {
    setValue(next);
    setErrorKey(next.trim() === '' ? null : validateApprovedInput(next).errorKey);
  }, []);

  const handleCommit = useCallback(() => {
    setIsFocused(false);

    if (value.trim() === '') {
      setErrorKey(null);
      setValue(formatted);
      return;
    }

    const { errorKey: nextErrorKey, parsedHours } = validateApprovedInput(value);

    // Invalid input — keep the error AND the raw text so the user can correct it rather than
    // silently losing what they typed. The effect above leaves `value` alone while it errors.
    if (nextErrorKey !== null || parsedHours === null) {
      setErrorKey(nextErrorKey);
      return;
    }

    setErrorKey(null);
    if (Math.round(parsedHours * 100) !== Math.round(hours * 100)) {
      onCommit(parsedHours);
    } else {
      setValue(formatted);
    }
  }, [value, hours, formatted, onCommit]);

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1">
        <ApprovedValueLabel tooltip={labelTooltip}>{label}</ApprovedValueLabel>
        <Input
          type="text"
          inputMode="text"
          className={cn(
            'h-6 min-w-0 flex-1 px-1.5 text-xs',
            errorKey && 'border-destructive focus-visible:ring-destructive'
          )}
          value={value}
          disabled={isReadOnly}
          aria-invalid={!!errorKey}
          placeholder={i18n.t('authenticated.timeTracking:approvals.columns.approvedPlaceholder')}
          aria-label={ariaLabel}
          onFocus={handleFocus}
          onChange={e => handleChange(e.target.value)}
          onBlur={handleCommit}
          onKeyDown={e => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          onClick={e => e.stopPropagation()}
        />
      </div>
      {errorKey && (
        <span className="text-[10px] leading-tight text-destructive">{i18n.t(errorKey)}</span>
      )}
    </div>
  );
}

interface EditableApprovedHoursCellProps {
  entry: ApprovalTimeEntry;
  meta: ApprovalsTableMeta | undefined;
}

/**
 * The Approved cell of a worklog row: two stacked inputs, one per purpose.
 *
 * Approved hours are split because an organization may invoice a client for more (or fewer) hours
 * than it pays the member for. Both live in this ONE column rather than two, so the table stays
 * scannable; the Notes column stays a single field, since one note covers the whole decision.
 */
function EditableApprovedHoursCellComponent({ entry, meta }: EditableApprovedHoursCellProps) {
  const isApproved = entry.status === TimeEntryStatus.APPROVED;
  // From the store (keyed by entry id) so the edit survives remount/re-derivation. An
  // APPROVED entry is read-only and server-owned: never let a leftover pending edit mask
  // the hours that were actually saved.
  const pendingEdit = useApprovalsStore(s => s.editedEntryValues[entry.id]);
  const edited = isApproved ? undefined : pendingEdit;

  // Unparseable text this row held before it was last unmounted (collapse, virtualization,
  // filter round-trip). An APPROVED row is server-owned and has no editable text to restore.
  const invalidApprovedInputs = useApprovalsStore(s => s.invalidApprovedInputs);
  const storedInvoiceValue = isApproved
    ? undefined
    : getInvalidApprovedInputValue(invalidApprovedInputs, entry.id, ApprovedHoursField.Invoice);
  const storedBillValue = isApproved
    ? undefined
    : getInvalidApprovedInputValue(invalidApprovedInputs, entry.id, ApprovedHoursField.Bill);

  const handleInvoiceCommit = useCallback(
    (hours: number) => meta?.onApprovedForInvoiceHoursChange(entry, hours),
    [entry, meta]
  );

  const handleBillCommit = useCallback(
    (hours: number) => meta?.onApprovedForBillHoursChange(entry, hours),
    [entry, meta]
  );

  const handleInvoiceValidity = useCallback(
    (rawValue: string | null) =>
      meta?.onApprovedHoursValidityChange(entry, ApprovedHoursField.Invoice, rawValue),
    [entry, meta]
  );

  const handleBillValidity = useCallback(
    (rawValue: string | null) =>
      meta?.onApprovedHoursValidityChange(entry, ApprovedHoursField.Bill, rawValue),
    [entry, meta]
  );

  // gap-1.5 (not gap-0.5): the inputs render a focus ring outside their border box, which
  // visually collided with the neighbouring field at a tighter gap.
  return (
    <div className="flex flex-col gap-1.5">
      <ApprovedDurationInput
        label={i18n.t('authenticated.timeTracking:approvals.columns.approvedInvoiceLabel')}
        labelTooltip={i18n.t('authenticated.timeTracking:approvals.columns.approvedInvoiceTooltip')}
        ariaLabel={i18n.t('authenticated.timeTracking:approvals.columns.approvedInvoiceAriaLabel')}
        hours={edited?.approvedForInvoiceHours ?? entry.approvedForInvoiceHours}
        storedInvalidValue={storedInvoiceValue}
        isReadOnly={isApproved}
        onCommit={handleInvoiceCommit}
        onValidityChange={handleInvoiceValidity}
      />
      <ApprovedDurationInput
        label={i18n.t('authenticated.timeTracking:approvals.columns.approvedBillLabel')}
        labelTooltip={i18n.t('authenticated.timeTracking:approvals.columns.approvedBillTooltip')}
        ariaLabel={i18n.t('authenticated.timeTracking:approvals.columns.approvedBillAriaLabel')}
        hours={edited?.approvedForBillHours ?? entry.approvedForBillHours}
        storedInvalidValue={storedBillValue}
        isReadOnly={isApproved}
        onCommit={handleBillCommit}
        onValidityChange={handleBillValidity}
      />
    </div>
  );
}

/**
 * Memoized: the cell holds local input state and runs effects, so it must not re-render
 * when an unrelated row's selection changes. Props (`entry`/`meta`/`rowId`) are kept stable
 * by the table (stable row identity + memoized `meta`).
 */
export const EditableApprovedHoursCell = memo(EditableApprovedHoursCellComponent);
